import { motion } from 'framer-motion';
import {
  Shield,
  TrendingUp,
  Users,
  Zap,
  ArrowRight,
  AlertCircle,
  CheckCircle,
  Map,
  Target,
  Lightbulb,
  DollarSign,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import Breadcrumb from '../../components/Breadcrumb';
import PageHero from '../../components/PageHero';
import { getPageHeroImageUrl } from '../../config/cloudflareAssets';

export default function PortersFiveForcesPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const forces = [
    {
      force: 'Threat of New Entrants',
      icon: Users,
      description:
        'How easy is it for new competitors to enter your market? High threat means lower long-term profitability as new players erode market share and drive down prices.',
      factors: [
        'Capital requirements',
        'Economies of scale',
        'Brand loyalty',
        'Regulatory barriers',
        'Access to distribution',
        'Patents and proprietary technology',
      ],
      questions: [
        'What barriers prevent new entrants?',
        'Are those barriers sustainable?',
        'How quickly could a well-funded competitor enter?',
        'Do incumbents have cost advantages?',
      ],
    },
    {
      force: 'Bargaining Power of Suppliers',
      icon: Zap,
      description:
        'How much power do your suppliers have over pricing and terms? Strong suppliers can extract profits through higher prices, reduced quality, or unfavorable terms.',
      factors: [
        'Number of suppliers',
        'Switching costs',
        'Uniqueness of inputs',
        'Importance to supplier',
        'Forward integration threat',
        'Substitute inputs available',
      ],
      questions: [
        'How concentrated is the supplier market?',
        'Could we switch suppliers easily?',
        'Do suppliers have leverage?',
        'Can suppliers bypass us and sell direct?',
      ],
    },
    {
      force: 'Bargaining Power of Buyers',
      icon: Shield,
      description:
        'How much power do your customers have to negotiate? Powerful buyers can force prices down, demand higher quality, and play competitors against each other.',
      factors: [
        'Customer concentration',
        'Product differentiation',
        'Switching costs',
        'Price sensitivity',
        'Backward integration threat',
        'Information availability',
      ],
      questions: [
        'Are customers price sensitive?',
        'How much switching cost exists?',
        'Can customers easily go elsewhere?',
        'Do customers have good information about alternatives?',
      ],
    },
    {
      force: 'Threat of Substitutes',
      icon: TrendingUp,
      description:
        'How easily could customers switch to alternative solutions? Substitutes limit profit potential by creating a price ceiling and forcing you to differentiate.',
      factors: [
        'Relative price/performance',
        'Switching costs',
        'Customer loyalty',
        'Emerging technologies',
        'Substitute quality and features',
      ],
      questions: [
        'What alternatives exist?',
        'Are substitutes improving faster than our product?',
        'What would trigger customers to switch?',
        'How do we compare on total cost?',
      ],
    },
    {
      force: 'Competitive Rivalry',
      icon: AlertCircle,
      description:
        'How intense is competition among existing players? High rivalry leads to price competition, aggressive marketing, and innovation pressure that erodes profitability.',
      factors: [
        'Number of competitors',
        'Market growth rate',
        'Product differentiation',
        'Exit barriers',
        'Fixed costs',
        'Strategic stakes',
      ],
      questions: [
        'How many serious competitors exist?',
        'Is competition based on price or value?',
        'Is the market consolidating or fragmenting?',
        'Can competitors easily match our moves?',
      ],
    },
  ];

  const industryExample = {
    industry: 'Cloud Storage (2026)',
    forces: [
      {
        name: 'New Entrants',
        rating: 'Medium',
        reasoning:
          'High capital requirements for global infrastructure but low barriers for niche players with specialized features',
      },
      {
        name: 'Supplier Power',
        rating: 'Low',
        reasoning: 'Many data center, hardware, and bandwidth options available globally',
      },
      {
        name: 'Buyer Power',
        rating: 'High',
        reasoning:
          'Low switching costs, many comparable alternatives, highly price-sensitive customers',
      },
      {
        name: 'Substitutes',
        rating: 'Medium',
        reasoning:
          'On-premise solutions declining but still viable for security-conscious enterprises',
      },
      {
        name: 'Rivalry',
        rating: 'Very High',
        reasoning:
          'Dominated by AWS, Azure, Google Cloud - intense price competition and feature race',
      },
    ],
  };

  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb
            items={[
              { label: 'Strategic Framework', path: '/strategic-framework' },
              { label: 'External Assessment', path: '/external-assessment' },
              { label: "Porter's Five Forces" },
            ]}
          />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={
          <span className="inline-flex items-center px-4 py-2 bg-white/90 text-blue-800 rounded-full text-sm font-semibold backdrop-blur-sm">
            <Map className="w-4 h-4 mr-2" />
            External Assessment Tool 1 of 4
          </span>
        }
        title="See what shapes profit in your industry"
        subtitle="Use Porter’s Five Forces to understand the pressures around your business. Identify where bargaining power sits, where competition intensifies, and what your strategy should address."
      >
        <Link
          to="/sign-up"
          className="public-primary-button inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90"
        >
          Assess your industry
        </Link>
      </PageHero>

      {/* The Five Forces */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">
              Understand the pressures behind your performance
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Competitors are only part of the picture. Explore the five forces together to see what
              influences margins, market access, and your room to compete.
            </p>
          </motion.div>
          <div className="space-y-8 max-w-5xl mx-auto">
            {forces.map((force, i) => (
              <motion.div
                key={i}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="public-card bg-white p-8 rounded-xl border border-gray-200"
              >
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-600 p-3 rounded-lg">
                    <force.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3">{force.force}</h3>
                    <p className="text-gray-700 mb-6 text-lg">{force.description}</p>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold mb-3 text-gray-900">
                          Key Factors to Analyze:
                        </h4>
                        <ul className="space-y-2">
                          {force.factors.map((f, j) => (
                            <li key={j} className="text-sm text-gray-600 flex items-start">
                              <span className="text-blue-500 mr-2 font-bold">•</span>
                              {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-3 text-gray-900">Critical Questions:</h4>
                        <ul className="space-y-2">
                          {force.questions.map((q, j) => (
                            <li key={j} className="text-sm text-gray-600 flex items-start">
                              <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                              {q}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Conduct Analysis */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">How to Conduct a Five Forces Analysis</h2>
              <p className="text-xl text-gray-600">
                Follow this systematic approach to understand your industry's competitive dynamics
              </p>
            </motion.div>
            <div className="space-y-6">
              {[
                {
                  step: '1',
                  title: 'Define Your Industry Scope',
                  description:
                    'Be specific about which industry segment you\'re analyzing. Are you in "cloud storage" or "enterprise cloud storage for healthcare"? The narrower your definition, the more useful your analysis.',
                  tip: 'Your industry definition should match how customers think about alternatives, not how you organize internally.',
                },
                {
                  step: '2',
                  title: 'Gather Data for Each Force',
                  description:
                    "Research each of the five forces systematically. Look at competitor financial reports, industry research, customer surveys, and supplier concentration data. Don't rely on assumptions or gut feel.",
                  tip: "Use a rating scale (Low/Medium/High or 1-5) to quantify each force's strength for consistency.",
                },
                {
                  step: '3',
                  title: 'Identify the Dominant Forces',
                  description:
                    'Not all five forces are equally important in every industry. Which 2-3 forces have the biggest impact on profitability? These are where you should focus your strategic attention and resources.',
                  tip: 'The strongest forces determine where you need defensive strategies or differentiation investments.',
                },
                {
                  step: '4',
                  title: 'Spot Changes Over Time',
                  description:
                    "Industry structure isn't static. Which forces are getting stronger or weaker? Technology, regulation, market maturity, or globalization can shift the balance of power dramatically over time.",
                  tip: 'Run this analysis annually or whenever major industry events occur (new entrants, M&A, tech disruption).',
                },
                {
                  step: '5',
                  title: 'Formulate Strategic Responses',
                  description:
                    'For each strong force, develop strategies to reduce its impact or exploit opportunities. Can you raise switching costs? Create barriers to entry? Differentiate to reduce buyer power? Each force demands specific strategic responses.',
                  tip: "Your strategy should address your industry's unique force configuration, not generic best practices from other industries.",
                },
              ].map((step, index) => (
                <motion.div
                  key={index}
                  initial={false}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                      {step.step}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                      <p className="text-gray-700 mb-3">{step.description}</p>
                      <div className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-500">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-blue-700">Pro Tip:</span> {step.tip}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industry Example */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">
              Industry Example: {industryExample.industry}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              See how Porter's Five Forces applies to a real industry and what strategic
              implications emerge
            </p>
          </motion.div>
          <div className="public-card max-w-4xl mx-auto bg-white p-8 rounded-xl border border-gray-200">
            <div className="space-y-4 mb-6">
              {industryExample.forces.map((f, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div className="flex-1">
                    <h3 className="font-bold text-lg mb-1">{f.name}</h3>
                    <p className="text-sm text-gray-600">{f.reasoning}</p>
                  </div>
                  <div
                    className={`px-4 py-2 rounded-full font-semibold text-sm ${f.rating === 'Very High' || f.rating === 'High' ? 'bg-red-100 text-red-700' : f.rating === 'Medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}
                  >
                    {f.rating}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                <Lightbulb className="w-5 h-5 mr-2 text-blue-600" />
                Overall Assessment:
              </h4>
              <p className="text-gray-700 leading-relaxed">
                The cloud storage industry faces intense competitive pressure from multiple angles.
                High buyer power and fierce rivalry make it challenging to maintain margins unless
                you achieve massive scale (like AWS) or create strong differentiation through
                specialized features, security certifications, or vertical integration. New entrants
                face capital barriers but can succeed in niches. The key strategic insight: avoid
                head-to-head price competition with giants—find underserved segments or
                differentiate dramatically.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Implications */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Strategic Implications & Responses</h2>
              <p className="text-xl text-gray-600">
                How to use your Five Forces analysis to shape winning strategy
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  icon: Shield,
                  title: 'Build Barriers to Entry',
                  description:
                    "If new entrants threaten your market, invest in creating sustainable competitive advantages that newcomers can't easily replicate. This protects your market share and pricing power long-term.",
                  examples: [
                    'Patents and IP protection',
                    'Network effects and platforms',
                    'High capital requirements',
                    'Regulatory compliance expertise',
                    'Exclusive partnerships',
                  ],
                },
                {
                  icon: Zap,
                  title: 'Reduce Supplier Power',
                  description:
                    'If suppliers have too much leverage, develop alternative sources, standardize inputs, or build flexibility into operations. In extreme cases, consider backward integration.',
                  examples: [
                    'Multiple supplier relationships',
                    'Standardize components',
                    'Strategic partnerships',
                    'Vertical integration',
                    'Long-term contracts',
                  ],
                },
                {
                  icon: Users,
                  title: 'Reduce Buyer Power',
                  description:
                    "If customers have too much negotiating power, increase switching costs through integration, customization, or unique value that's difficult to replace. Or target less price-sensitive segments.",
                  examples: [
                    'Increase differentiation',
                    'Add switching costs',
                    'Target premium segments',
                    'Loyalty and rewards programs',
                    'Custom integrations',
                  ],
                },
                {
                  icon: Target,
                  title: 'Defend Against Substitutes',
                  description:
                    'If substitutes threaten your business model, either improve your value proposition dramatically to justify premium pricing or pivot toward the substitute before competitors do.',
                  examples: [
                    'Superior performance',
                    'Better customer experience',
                    'Lower total cost of ownership',
                    'Bundled solutions',
                    'Continuous innovation',
                  ],
                },
                {
                  icon: TrendingUp,
                  title: 'Differentiate from Rivals',
                  description:
                    'If competitive rivalry is intense, avoid head-to-head competition on price. Find niches, create unique value, or change the basis of competition from price to service, quality, or innovation.',
                  examples: [
                    'Niche specialization',
                    'Unique features or technology',
                    'Superior customer service',
                    'Brand positioning',
                    'Vertical focus',
                  ],
                },
                {
                  icon: DollarSign,
                  title: 'Choose Profitable Segments',
                  description:
                    'Not all segments face identical forces. Some may have weaker buyer power, fewer substitutes, or higher entry barriers. Focus resources where industry structure is more favorable.',
                  examples: [
                    'Premium customer segments',
                    'Underserved niches',
                    'Growing geographic markets',
                    'Less competitive verticals',
                    'Early adopter segments',
                  ],
                },
              ].map((impl, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <impl.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold">{impl.title}</h3>
                  </div>
                  <p className="text-gray-700 mb-4">{impl.description}</p>
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2">Tactics:</p>
                    <ul className="space-y-1">
                      {impl.examples.map((ex, i) => (
                        <li key={i} className="text-sm text-gray-600 flex items-start">
                          <CheckCircle className="w-3 h-3 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                          {ex}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Common Mistakes to Avoid</h2>
              <p className="text-xl text-gray-600">
                Learn from others' errors when applying Porter's framework
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  mistake: 'Analyzing the Wrong Industry',
                  description:
                    'Defining your industry too broadly or narrowly leads to meaningless analysis. "Technology" is too broad. "SaaS PM tools for remote teams under 50" might be too narrow.',
                  fix: 'Define industry boundaries by customer substitution behavior—what do customers actually compare when deciding?',
                },
                {
                  mistake: 'Static One-Time Analysis',
                  description:
                    'Treating Five Forces as a checkbox exercise. Industries evolve constantly. New technologies, regulations, or business models can rapidly shift force dynamics.',
                  fix: 'Reassess annually or when major industry changes occur (new entrants, M&A, regulatory shifts, tech disruptions).',
                },
                {
                  mistake: 'Ignoring Force Interactions',
                  description:
                    'Analyzing each force in isolation when they actually interact. High rivalry reduces supplier power. Low entry barriers increase rivalry. Strong buyer power attracts substitutes.',
                  fix: 'Look for relationships between forces. How does a change in one force ripple through and impact the others?',
                },
                {
                  mistake: 'Confusing Industry vs. Company',
                  description:
                    "Porter's framework analyzes industry attractiveness, not your company's competitive position. These are related but distinct questions requiring different tools.",
                  fix: 'Use Five Forces for industry selection and understanding. Use SWOT, resources, and capabilities for company analysis.',
                },
                {
                  mistake: 'Analysis Without Action',
                  description:
                    'Documenting the forces beautifully without translating insights into strategic action. Analysis without application is academic exercise, not strategy.',
                  fix: 'For every strong force identified, define 2-3 specific strategic responses with owners and timelines. Build an action plan.',
                },
                {
                  mistake: 'Ignoring Segment Differences',
                  description:
                    'Assuming all market segments face identical forces. Enterprise vs. SMB, different geographies, or customer types often face very different competitive dynamics.',
                  fix: 'Run separate Five Forces analyses for strategically distinct segments. You may need different strategies for each.',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-red-200"
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <h3 className="text-lg font-bold text-gray-900">{item.mistake}</h3>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm">{item.description}</p>
                  <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-green-700">✓ Fix:</span> {item.fix}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Put the framework to work */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-sm font-semibold text-blue-700 mb-3">
                YOUR NEXT STEP IN STRATPLAN
              </p>
              <h2 className="text-3xl font-bold mb-5">
                Turn industry pressure into strategic choices
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Bring this framework into your company’s plan. Work through the questions, record
                your decisions, and connect them to the strategy you are building.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">01</p>
                <h3 className="text-xl font-bold mb-3">Map the five forces</h3>
                <p className="text-gray-600">
                  Record the evidence behind rivalry, entry barriers, substitutes, and buyer and
                  supplier power.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">02</p>
                <h3 className="text-xl font-bold mb-3">Identify the pressure points</h3>
                <p className="text-gray-600">
                  Distinguish the forces you can influence from those your business needs to adapt
                  to.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">03</p>
                <h3 className="text-xl font-bold mb-3">Choose a response</h3>
                <p className="text-gray-600">
                  Connect the strongest pressures to decisions about positioning, partnerships, and
                  investment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Topics */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Related Strategic Tools</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link
                to="/external-assessment/pestel-analysis"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <h3 className="text-xl font-bold mb-2 text-blue-600">PESTEL Analysis</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Understand macro-environmental factors beyond immediate competitive forces
                </p>
                <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/internal-assessment/swot-analysis"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <h3 className="text-xl font-bold mb-2 text-purple-600">SWOT Analysis</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Combine external threats and opportunities with internal strengths and weaknesses
                </p>
                <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/strategy-formulation/competitive-strategy"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <h3 className="text-xl font-bold mb-2 text-indigo-600">Competitive Strategy</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Choose how to compete (cost, differentiation, focus) based on your force analysis
                </p>
                <div className="flex items-center text-indigo-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/external-assessment"
                className="inline-flex items-center justify-center bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50"
              >
                ← Back to External Assessment
              </Link>
              <Link
                to="/external-assessment/pestel-analysis"
                className="inline-flex items-center justify-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
              >
                Next: PESTEL Analysis <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Start your plan */}
      <section className="public-section public-cta py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-5">
            Assess your industry in StratPlan
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Connect the strongest pressures to decisions about positioning, partnerships, and
            investment. Start with your company’s context and build a plan you can put to work.
          </p>
          <Link
            to="/sign-up"
            className="public-primary-button inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700"
          >
            Assess your industry <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
